import { Agent } from '@/DTOS/Agent/Agent';
import { Divider, Drawer, Table } from 'antd';
import moment from 'moment';
import React from 'react';
import { Column } from '../../../../utils/column';

interface InterventionFileDrawerProps {
  handleModalInformationClose: () => void;
  modalInformation: boolean;
  selectedRecord: any | null;
}

const InterventionFileDrawer: React.FC<InterventionFileDrawerProps> = ({
  handleModalInformationClose,
  modalInformation,
  selectedRecord,
}) => {
  const companyDetailsColumns: Column[] = [
    {
      title: 'Nume Obiectiv',
      dataIndex: '',
      key: 'workPlaceName',
      render: (record: any) => {
        return <strong>{record?.metadata?.workPlace?.name ?? record?.workPlace?.name}</strong>;
      },
    },
    {
      title: 'Adresa Obiectiv',
      dataIndex: '',
      key: 'workPlaceAddress',
      render: (record: any) => {
        return (
          <>
            {record?.metadata?.workPlace?.name ?? record?.workPlace?.name}{' '}
            {record?.metadata?.workPlace?.address ?? record?.workPlace?.address}
          </>
        );
      },
      width: 300,
    },
    {
      title: 'Firma/C.U.I.',
      key: 'company',
      render: (record: any) => {
        const company = record?.metadata?.company ?? record?.company;
        return <div>{company?.name}</div>;
      },
      dataIndex: '',
      width: 300,
    },
    {
      title: 'Reprezentant',
      dataIndex: 'agent',
      key: 'agentDetails',
      render: (agent: Agent) => (
        <>
          <div>Nume: {agent.name}</div>
          <div>Functie: {agent.role[0]}</div>
          <div>Email: {agent.email[0]}</div>
          <div>Telefon: {agent.phone[0]}</div>
        </>
      ),
    },
  ];

  const informationColumns: Column[] = [
    {
      title: 'Tip Manopera',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Aparat Adaugat',
      dataIndex: 'deviceRepaired',
      key: 'deviceRepaired',
      render: (value: any) => (value ? 'Da' : 'Nu'),
    },
    {
      title: 'Preluat in service',
      dataIndex: 'takenIntoService',
      key: 'takenIntoService',
      render: (value: any) => (value ? 'Da' : 'Nu'),
    },
    {
      title: 'Defect Reclamat',
      dataIndex: 'reportedDefect',
      key: 'reportedDefect',
    },
    {
      title: 'Defect Constatat',
      dataIndex: 'ascertainment',
      key: 'ascertainment',
    },
  ];

  const laborTimeAndLocationColumns: Column[] = [
    {
      title: 'Timp Manopera',
      dataIndex: 'laborTime',
      key: 'laborTime',
      render: (value: any) => value + ' min',
    },
    {
      title: 'Locatie GPS',
      dataIndex: '',
      key: 'locationGPS',
      render: (record: any) => (
        <div>
          <a
            href={`https://www.waze.com/ro/live-map/directions?to=ll.${record.latitude},${record.longitude}`}
            target='_blank'
            rel='noopener noreferrer'
          >
            Waze: {record.latitude}, {record.longitude}
          </a>
          <br />
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${record.latitude},${record.longitude}`}
            target='_blank'
            rel='noopener noreferrer'
          >
            Google: {record.latitude}, {record.longitude}
          </a>
        </div>
      ),
    },
  ];

  const recomandationsColumns: Column[] = [
    {
      title: 'OPERATII',
      dataIndex: 'operations',
      key: 'operations',
    },
    {
      title: 'Consum',
      dataIndex: 'consumptionDetails',
      key: 'consumptionDetails',
    },
    {
      title: 'Recomandari',
      dataIndex: 'recommendationDetails',
      key: 'recommendationDetails',
    },
    {
      title: 'Observatii din partea persoanei de contact',
      dataIndex: 'contactPersonObservations',
      key: 'contactPersonObservations',
    },
  ];

  const requestColumns: Column[] = [
    {
      title: 'Data primirii',
      dataIndex: 'deliveryDate',
      key: 'deliveryDate',
      render: (text: string) => moment(text).format('DD.MM.YYYY'),
    },
    {
      title: 'Primit de',
      dataIndex: 'agent',
      key: 'agentDetails',
      render: (agent: Agent) => (
        <>
          <div>Nume: {agent.name}</div>
          <div>Functie: {agent.role[0]}</div>
          <div>Email: {agent.email[0]}</div>
          <div>Telefon: {agent.phone[0]}</div>
        </>
      ),
    },
    {
      title: 'Semnatura',
      dataIndex: 'representativeSignature',
      key: 'representativeSignature',
      render: (value: any) =>
        value ? <img alt='representativeSignature' style={{ height: '70px' }} src={value} /> : '-',
    },
  ];

  const reprezentativeColumns: Column[] = [
    {
      title: 'Reprezentant Helion',
      dataIndex: 'helionEngineers',
      key: 'helionEngineers',
      render: (helionEngineers: any) => (
        <>
          {helionEngineers?.length > 0 ? (
            helionEngineers?.map((engineer: any, index: any) => {
              return <div key={index}>{'[' + engineer?.user?.name + ']'}</div>;
            })
          ) : (
            <div>----</div>
          )}
        </>
      ),
    },
    {
      title: 'Semnatura',
      dataIndex: 'signature',
      key: 'signature',
      render: (value: any) =>
        value && value != '' ? <img alt='signature' style={{ height: '70px' }} src={value} /> : '-',
    },
  ];

  const systemTypesColumns: Column[] = [
    {
      title: 'Tip sistem',
      dataIndex: ['systemType', 'name'],
      key: 'systemTypesName',
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'systemTypeModel',
    },
    {
      title: 'Numar componente',
      dataIndex: 'devicesNumber',
      key: 'systemTypeDevicesNumber',
    },
    {
      title: 'Sistem instalat de',
      dataIndex: 'installBy',
      key: 'systemTypeInstallBy',
    },
    {
      title: 'Operatiuni',
      dataIndex: '',
      key: '',
      render: () => (
        <>
          <div>* Verificarea vizuala a intregului sistem (fixare, pozitie, orientare camere video);</div>
          <div>* Verificarea functionalitatii inregistratorului video digital (DVR/HVR/NVR);</div>
          <div>* Verificarea softului de inregistrare/monitorizare</div>
        </>
      ),
    },
  ];

  return (
    <Drawer
      title={`Informatii suplimentare`}
      placement={'right'}
      onClose={handleModalInformationClose}
      open={modalInformation}
      width='50%'
      key={'drawer_right'}
    >
      <h2 style={{ textAlign: 'center' }}>
        FISA DE INTERVENTIE <br />
        Proces verbal de predare-primire Nr. Fisa/P.V: {selectedRecord?.number} <br />
        DATA: {moment(selectedRecord?.deliveryDate)?.format('DD.MM.YYYY')}
      </h2>
      <Divider />

      <Table
        bordered
        pagination={false}
        columns={companyDetailsColumns}
        dataSource={selectedRecord ? [selectedRecord] : []}
        scroll={{ x: true }}
        rowKey='id'
      />
      <Divider />
      <Table
        bordered
        pagination={false}
        columns={informationColumns}
        dataSource={selectedRecord ? [selectedRecord] : []}
        scroll={{ x: true }}
        rowKey='id'
      />
      <Divider />
      <Table
        bordered
        pagination={false}
        columns={laborTimeAndLocationColumns}
        dataSource={selectedRecord ? [selectedRecord] : []}
        scroll={{ x: true }}
        rowKey='id'
      />
      <Divider />
      <Table
        bordered
        pagination={false}
        columns={systemTypesColumns}
        dataSource={selectedRecord?.interventionFileSystemTypes ?? []}
        scroll={{ x: true }}
        rowKey='id'
      />

      <Divider />
      <Table
        bordered
        pagination={false}
        columns={recomandationsColumns}
        dataSource={selectedRecord ? [selectedRecord] : []}
        scroll={{ x: true }}
        rowKey='id'
      />
      <Divider />
      <Table
        bordered
        pagination={false}
        columns={reprezentativeColumns}
        dataSource={selectedRecord ? [selectedRecord] : []}
        scroll={{ x: true }}
        rowKey='id'
      />
      <Divider />
      <Table
        bordered
        pagination={false}
        columns={requestColumns}
        dataSource={selectedRecord ? [selectedRecord] : []}
        scroll={{ x: true }}
        rowKey='id'
      />
    </Drawer>
  );
};

export default InterventionFileDrawer;
